import { useMemo } from "react"
import { useTheme } from "next-themes"
import { mapValues } from "lodash"
import { getTailwindValue } from "~/utils/tailwind"
import type { DashStyleValue } from "highcharts"

export type ChartTheme = "light" | "dark"

/**
 * Resolved theme for chart color lookups. Colors are keyed by this string, never read from
 * `[data-theme]` DOM vars — those go stale on toggle (next-themes applies the attribute
 * after subscribers re-render). Same pattern as SuperChart's frame colors.
 */
export function useChartTheme(): ChartTheme {
  const { resolvedTheme } = useTheme()
  return resolvedTheme === "dark" ? "dark" : "light"
}

// Cached per theme: getTailwindValue is an uncached getComputedStyle read and the year
// helpers run inside loops.
const chartColorsCache = new Map<ChartTheme, ChartColors>()

function getChartColors(theme: ChartTheme): ChartColors {
  const cached = chartColorsCache.get(theme)
  if (cached) return cached
  const colors = buildChartColorValues(theme)
  // "" = SSR / stylesheet not loaded yet — don't cache
  if (colors.salesDemand) chartColorsCache.set(theme, colors)
  return colors
}

const buildChartColorValues = (theme: ChartTheme) => buildChartColors(theme, getTailwindValue)

type ChartColors = ReturnType<typeof buildChartColorValues>
type ChartColorKey = keyof ChartColors

/**
 * The Tailwind token behind each chart color for the given theme, e.g. `purpleheart-500`.
 * Same keys and same theme branching as {@link getChartColors} — both instantiate the one
 * token table below, so a name can never drift from the value it labels.
 */
export function getChartColorTokens(theme: ChartTheme): Record<ChartColorKey, string> {
  return buildChartColors(theme, (cssVar) => cssVar.replace("--color-", ""))
}

// Semantic chart series colors — single source of truth, mirrors the Figma chart tokens.
// `resolve` turns a CSS var into either its value (charts) or its token name (design system).
function buildChartColors<T>(theme: ChartTheme, resolve: (cssVar: string) => T) {
  const themed = (light: string, dark: string) => resolve(theme === "dark" ? dark : light)
  const salesDemand = themed("--color-purpleheart-500", "--color-purpleheart-400")
  const closingStock = themed("--color-teal-500", "--color-teal-400")
  const incomingStock = themed("--color-fuchsia-500", "--color-fuchsia-400")
  return {
    closingStock,
    // Suggested variants share their counterpart's color; dash or hatch marks them.
    closingStockSuggested: closingStock,
    projectedStock: themed("--color-fuchsia-900", "--color-fuchsia-200"),
    safetyStock: themed("--color-teal-900", "--color-teal-200"),
    incomingStock,
    incomingStockSuggested: incomingStock,
    nextDelivery: themed("--color-gray-400", "--color-gray-500"),
    overstockLevel: themed("--color-orange-700", "--color-orange-300"),
    salesDemand,
    netSales: themed("--color-violet-700", "--color-violet-400"),
    rawDemand: themed("--color-purpleheart-900", "--color-purpleheart-200"),
    matchPrediction: themed("--color-purpleheart-900", "--color-purpleheart-200"),
    expectedReturns: themed("--color-amber-900", "--color-amber-200"),
    // Sales year-over-year ramp: current + 4 prior years, progressively lighter
    yearCurrent: salesDemand,
    year1: themed("--color-purpleheart-400", "--color-purpleheart-300"),
    year2: themed("--color-purpleheart-300", "--color-purpleheart-200"),
    year3: themed("--color-purpleheart-200", "--color-purpleheart-100"),
    year4: themed("--color-purpleheart-100", "--color-purpleheart-50"),
    // Forecast-page series
    itemProfit: resolve("--color-violet-400"),
    preOrder: themed("--color-green-500", "--color-green-400"),
    // Overlaid on preOrder — deep/light split keeps the two column sets distinguishable
    preOrderComparison: themed("--color-green-900", "--color-green-200"),
    inboundPreOrder: themed("--color-amber-400", "--color-amber-300"),
    invalidPreOrder: resolve("--color-red-600"),
    unfulfillablePreOrder: themed("--color-rose-500", "--color-rose-400"),
    atRiskPreOrder: themed("--color-amber-500", "--color-amber-400"),
    forecastWithoutPreOrders: themed("--color-lavander-800", "--color-lavander-400"),
    // Demand's pre-order parts: confirmed vs expected, as Pre-order development draws them
    confirmedPreOrder: resolve("--color-purpleheart-500"),
    expectedPreOrder: resolve("--color-violet-500"),
    expectedReorder: resolve("--color-violet-300"),
    // Markdown impact: names match the Figma charts/* tokens
    markdownSales: themed("--color-amber-500", "--color-amber-400"),
    expectedMargin: themed("--color-gray-800", "--color-gray-200"),
    lastYearMargin: themed("--color-gray-500", "--color-gray-400"),
    fullPriceMargin: themed("--color-green-600", "--color-green-400"),
    // Seasonality planning: the comparison collection's sales, drawn as a tick per period
    comparisonSales: themed("--color-fuchsia-500", "--color-fuchsia-400"),
    // Supplementary haki chat palette — no dark-mode inverse
    orange: resolve("--color-orange-400"),
    lavender: resolve("--color-lavander-500"),
  }
}

/** Generic single-series colors for graphs without a semantic series term. */
export function getChartBaseColors(theme: ChartTheme): {
  base: string
  subtle: string
  contrast: string
} {
  return {
    base: getTailwindValue("--color-indigo-500"),
    subtle: getTailwindValue("--color-indigo-200"),
    contrast: getTailwindValue(theme === "dark" ? "--color-indigo-300" : "--color-indigo-950"),
  }
}

export function useChartBaseColors(): ReturnType<typeof getChartBaseColors> {
  const theme = useChartTheme()
  return useMemo(() => getChartBaseColors(theme), [theme])
}

// Color for a year-comparison series: offset 0 = current year, ±1–±4 = progressively
// lighter tints. Future and past years mirror each other (offset +1 and -1 share year1).
// Sales-only — forecast/demand/preorder series use their own term color.
export function getYearColor(yearOffset: number, theme: ChartTheme): string {
  const chartColors = getChartColors(theme)
  const ramp = [
    chartColors.yearCurrent,
    chartColors.year1,
    chartColors.year2,
    chartColors.year3,
    chartColors.year4,
  ]
  return ramp[Math.min(Math.abs(yearOffset), ramp.length - 1)] ?? chartColors.year4
}

/** Theme-bound {@link getYearColor}; re-renders the caller on theme change. */
export function useYearColor(): (yearOffset: number) => string {
  const theme = useChartTheme()
  return useMemo(() => (yearOffset: number) => getYearColor(yearOffset, theme), [theme])
}

// Season comparison: ramp color by recency, plus a heavier stroke on the newest year and on
// the forecast so they read as the primary lines.
export function getYearSeriesStyle(
  yearsBack: number,
  theme: ChartTheme,
  isForecast = false,
): {
  color: string
  lineWidth: number
} {
  if (isForecast) {
    return { color: getYearColor(1, theme), lineWidth: 3.5 }
  }
  return {
    color: getYearColor(yearsBack, theme),
    lineWidth: yearsBack === 0 ? 3 : 2,
  }
}

/** Theme-bound {@link getYearSeriesStyle}; re-renders the caller on theme change. */
export function useYearSeriesStyle(): (
  yearsBack: number,
  isForecast?: boolean,
) => ReturnType<typeof getYearSeriesStyle> {
  const theme = useChartTheme()
  return useMemo(
    () =>
      (yearsBack: number, isForecast = false) =>
        getYearSeriesStyle(yearsBack, theme, isForecast),
    [theme],
  )
}

/**
 * One style per chart series. The `Record<ChartSeriesId, ChartSeriesStyle>` typing forces
 * every new id to register a color and a dashStyle or the build fails.
 */
export type ChartSeriesId =
  | "closingStock"
  | "closingStockSuggested"
  | "projectedStock"
  | "safetyStock"
  | "incomingStock"
  | "incomingStockSuggested"
  | "nextDeliverySuggested"
  | "overstockLevel"
  | "sales"
  | "demand"
  | "demandColumn"
  | "netSales"
  | "rawDemand"
  | "matchPrediction"
  | "expectedReturns"
  | "itemProfit"
  | "futureItemProfit"
  | "preOrder"
  | "preOrderComparison"
  | "futurePreOrders"
  | "inboundPreOrder"
  | "invalidPreOrder"
  | "unfulfillablePreOrder"
  | "atRiskPreOrder"
  | "forecastWithoutPreOrders"
  | "confirmedPreOrders"
  | "expectedPreOrders"
  | "expectedReorders"
  | "markdownSales"
  | "expectedMargin"
  | "lastYearMargin"
  | "fullPriceMargin"
  | "comparisonSales"

/** SVG pattern tile for a hatched series fill. */
export interface ChartSeriesPattern {
  path: string
  width: number
  height: number
  strokeWidth: number
}

export interface ChartSeriesStyle {
  color: string
  dashStyle: DashStyleValue
  pattern?: ChartSeriesPattern
}

// Two lines per 6×6 tile (x+y ≡ 3 mod 6, 1px overhang) tile seamlessly at ~4.2px spacing.
export const projectedColumnHatch: ChartSeriesPattern = {
  path: "M -1 4 L 4 -1 M 2 7 L 7 2",
  width: 6,
  height: 6,
  strokeWidth: 2,
}

const HATCH_ANGLE = "135deg"

/**
 * {@link projectedColumnHatch} as a CSS background, for HTML surfaces that can't take an SVG
 * pattern fill. Stroke and spacing come off the tile — its 45° lines sit `width / √2` apart — so
 * the two fills stay identical as the tile changes.
 */
export function projectedHatchBackground(color: string): string {
  const { strokeWidth, width } = projectedColumnHatch
  return `repeating-linear-gradient(${HATCH_ANGLE}, ${color} 0 ${strokeWidth}px, transparent ${strokeWidth}px ${width / Math.SQRT2}px)`
}

/** The narrowest a hatched surface can be and still show a full stripe period. */
export const HATCH_PERIOD_PX = projectedColumnHatch.width / Math.SQRT2

// Convention: projected variants (suggested/future) share their base series' hue; a dashed
// line marks projected splines, a hatched fill marks projected columns. Markers are always
// circles — hue plus dash/hatch are the whole vocabulary.
function buildChartSeriesConfig<T>(
  chartColors: Record<ChartColorKey, T>,
): Record<ChartSeriesId, { color: T; dashStyle: DashStyleValue; pattern?: ChartSeriesPattern }> {
  return {
    closingStock: { color: chartColors.closingStock, dashStyle: "Solid" },
    closingStockSuggested: { color: chartColors.closingStockSuggested, dashStyle: "Dash" },
    projectedStock: { color: chartColors.projectedStock, dashStyle: "Solid" },
    safetyStock: { color: chartColors.safetyStock, dashStyle: "Solid" },
    incomingStock: { color: chartColors.incomingStock, dashStyle: "Solid" },
    incomingStockSuggested: {
      color: chartColors.incomingStockSuggested,
      dashStyle: "Dash",
      pattern: projectedColumnHatch,
    },
    nextDeliverySuggested: {
      color: chartColors.nextDelivery,
      dashStyle: "Dash",
      pattern: projectedColumnHatch,
    },
    overstockLevel: { color: chartColors.overstockLevel, dashStyle: "Solid" },
    sales: { color: chartColors.salesDemand, dashStyle: "Solid" },
    demand: { color: chartColors.salesDemand, dashStyle: "Dash" },
    demandColumn: {
      color: chartColors.salesDemand,
      dashStyle: "Dash",
      pattern: projectedColumnHatch,
    },
    netSales: { color: chartColors.netSales, dashStyle: "Solid" },
    rawDemand: { color: chartColors.rawDemand, dashStyle: "Dash" },
    matchPrediction: { color: chartColors.matchPrediction, dashStyle: "Solid" },
    expectedReturns: {
      color: chartColors.expectedReturns,
      dashStyle: "Dash",
      pattern: projectedColumnHatch,
    },
    itemProfit: { color: chartColors.itemProfit, dashStyle: "Solid" },
    futureItemProfit: { color: chartColors.itemProfit, dashStyle: "Dash" },
    preOrder: { color: chartColors.preOrder, dashStyle: "Solid" },
    preOrderComparison: { color: chartColors.preOrderComparison, dashStyle: "Solid" },
    futurePreOrders: { color: chartColors.preOrder, dashStyle: "Dash" },
    inboundPreOrder: { color: chartColors.inboundPreOrder, dashStyle: "Dash" },
    invalidPreOrder: { color: chartColors.invalidPreOrder, dashStyle: "Solid" },
    unfulfillablePreOrder: { color: chartColors.unfulfillablePreOrder, dashStyle: "Solid" },
    atRiskPreOrder: { color: chartColors.atRiskPreOrder, dashStyle: "Solid" },
    forecastWithoutPreOrders: { color: chartColors.forecastWithoutPreOrders, dashStyle: "Dash" },
    confirmedPreOrders: { color: chartColors.confirmedPreOrder, dashStyle: "Solid" },
    expectedPreOrders: {
      color: chartColors.expectedPreOrder,
      dashStyle: "Dash",
      pattern: projectedColumnHatch,
    },
    expectedReorders: {
      color: chartColors.expectedReorder,
      dashStyle: "Dash",
      pattern: projectedColumnHatch,
    },
    markdownSales: { color: chartColors.markdownSales, dashStyle: "Solid" },
    expectedMargin: { color: chartColors.expectedMargin, dashStyle: "Solid" },
    lastYearMargin: { color: chartColors.lastYearMargin, dashStyle: "Dash" },
    fullPriceMargin: { color: chartColors.fullPriceMargin, dashStyle: "Dash" },
    comparisonSales: { color: chartColors.comparisonSales, dashStyle: "Solid" },
  }
}

export function getChartSeriesConfig(theme: ChartTheme): Record<ChartSeriesId, ChartSeriesStyle> {
  return buildChartSeriesConfig(getChartColors(theme))
}

/**
 * The Tailwind token name behind each series' color, e.g. `purpleheart-500`. Documents the
 * palette on the design-system Graphs page — charts want {@link getChartSeriesConfig}.
 */
export function getChartSeriesColorTokens(theme: ChartTheme): Record<ChartSeriesId, string> {
  return mapValues(buildChartSeriesConfig(getChartColorTokens(theme)), (style) => style.color)
}

/**
 * Series style config for the active theme. The hook subscription keeps rendered charts
 * updating on theme change.
 */
export function useChartSeriesConfig(): Record<ChartSeriesId, ChartSeriesStyle> {
  const theme = useChartTheme()
  return useMemo(() => getChartSeriesConfig(theme), [theme])
}

/** Theme-bound {@link getChartSeriesColorTokens}. */
export function useChartSeriesColorTokens(): Record<ChartSeriesId, string> {
  const theme = useChartTheme()
  return useMemo(() => getChartSeriesColorTokens(theme), [theme])
}

// Haki chat palette — one token per hue family, since several semantic tokens share a hue.
export function getHakiChartColors(theme: ChartTheme): string[] {
  const chartColors = getChartColors(theme)
  return [
    chartColors.closingStock, // teal
    chartColors.projectedStock, // fuchsia
    getTailwindValue("--color-blue-500"), // blue
    chartColors.salesDemand, // purpleheart
    chartColors.netSales, // dark purple
    chartColors.expectedReturns, // amber
    chartColors.unfulfillablePreOrder, // rose
    getTailwindValue("--color-green-600"), // green
    getTailwindValue("--color-mint-500"), // mint
    getTailwindValue("--color-red-600"), // red
    chartColors.orange, // orange
    chartColors.lavender, // lavender
  ]
}

/** Theme-bound {@link getHakiChartColors}; re-renders the caller on theme change. */
export function useHakiChartColors(): string[] {
  const theme = useChartTheme()
  return useMemo(() => getHakiChartColors(theme), [theme])
}
